import type React from "react";
import { LucideLaptop, LucideRocket, ChevronRight, Info, CheckCircle, AlertTriangle } from "lucide-react";
import { RadioSetOptions } from "./global/RadioSet";
import RadioSet from "./global/RadioSet";
import { FileUploadData } from "./global/FileUpload";
import { useEffect, useRef, useState } from "react";
import { FormButton } from "@/components/ui/FormButton";
import { dataApi, enrollmentApi } from "../services/api";
import DroppableFileUpload from "./global/DroppableFileUpload";
import { useGlobal } from "@/context/GlobalContext";
import { TechCheckStatus } from "@/core/models/global/DocumentUploadStatus";
import SystemCheckPanel, { type SystemCheckOutcome } from "./SystemCheckPanel";
import { useNavigate } from "react-router-dom";
import TechCheckSubmittedModal from "./TechCheckSubmittedModal";

const SUBMITTED_MODAL_DURATION_MS = 3000;

// The in-page system check now captures and attaches its own results image and determines whether
// requirements are met. Flip to true to restore the manual screenshot upload and Yes/No prompt.
const MANUAL_UPLOAD_ENABLED = false;

interface TechnologyCheckProps
{

}


const TechnologyCheck: React.FC<TechnologyCheckProps> = ({

}) =>
{
    const { getUser } = useGlobal();
    const navigate = useNavigate();
    const applicantId = getUser()?.applicantId ?? 0;

    var [uploadData, setUploadData] = useState(new FileUploadData());

    const [docStatus, setDocStatus] = useState<TechCheckStatus | null>(null);

    const [radioVal, setRadioVal] = useState<boolean | null>(null);

    const formRef = useRef<HTMLFormElement | null>(null);

    const [disableSubmit, setDisableSubmit] = useState(false);

    const [systemCheckStarted, setSystemCheckStarted] = useState(false);
    const [systemCheckBusy, setSystemCheckBusy] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [showSubmittedModal, setShowSubmittedModal] = useState(false);
    const [submitFailed, setSubmitFailed] = useState(false);

    useEffect(() =>
    {
        if (!showSubmittedModal || submitFailed) return;
        const t = setTimeout(() => navigate("/dashboard"), SUBMITTED_MODAL_DURATION_MS);
        return () => clearTimeout(t);
    }, [showSubmittedModal, submitFailed, navigate]);

    // Load the existing upload (if any), so a returning applicant sees their file as already uploaded instead of a blank dropzone
    useEffect(() =>
    {
        if (!applicantId) return;

        enrollmentApi
            .getTechCheckStatus(applicantId)
            .then((status) =>
            {
                if (status)
                {
                    setDocStatus(status);
                    setUploadData(new FileUploadData({ filename: status.fileName, alreadyUploaded: true }));
                    setRadioVal(status.meetsRequirements);
                }
            })
            .catch((err) => console.error("Failed to load Technology Check status", err));
    }, [applicantId]);

    useEffect(() =>
    {
        setDisableSubmit(!systemCheckStarted || !uploadData.filePicked || radioVal === null);
    }, [systemCheckStarted, uploadData.filePicked, radioVal]);

    const handleFileSelection = (fileData: FileUploadData) =>
    {
        // Any file change (a new pick, or removing the current one) breaks the association with
        // whatever answer was loaded for the previous document instance - drop the stale server
        // metadata and require a fresh Yes/No answer rather than carrying the old one over.
        setDocStatus(null);
        setRadioVal(null);
        setUploadData(fileData);
    };

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    {
        const { value } = e.target;

        const realValue =
            value === "true"
                ? true
                : value === "false"
                    ? false
                    : null;

        setRadioVal(realValue);
    };

    // Feeds the auto-captured results image through the same upload state the manual dropzone uses
    const handleSystemCheckComplete = ({ screenshot, meetsRequirements }: SystemCheckOutcome) =>
    {
        setDocStatus(null);
        setSubmitFailed(false);
        setUploadData(new FileUploadData({ filename: screenshot.name, file: screenshot }));
        setRadioVal(meetsRequirements);
    };

    const handleSystemCheckReset = () =>
    {
        setDocStatus(null);
        setSubmitFailed(false);
        setUploadData(new FileUploadData());
        setRadioVal(null);
    };

    // New, not-yet-saved results exist only when a fresh file was produced and an answer is set;
    // a previously saved upload loaded from the server has no File object.
    const hasResultsToSubmit = systemCheckStarted && !!uploadData.file && radioVal !== null;

    const submitResults = async () =>
    {
        if (submitting || !systemCheckStarted) return;
        setSubmitting(true);
        setSubmitFailed(false);
        try
        {
            await dataApi.submitTechCheck({
                filename: uploadData.filename,
                file: uploadData.file,
                meetsRequirements: radioVal
            });
            setShowSubmittedModal(true);
        }
        catch (err)
        {
            console.error("Failed to submit Technology Check results", err);
            setShowSubmittedModal(false);
            setSubmitFailed(true);
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) =>
    {
        e.preventDefault();
        await submitResults();
    };

    const handleSaveAndExit = async () =>
    {
        if (!hasResultsToSubmit)
        {
            navigate("/dashboard");
            return;
        }
        await submitResults();
    };

    return (
        <form ref={formRef} onSubmit={handleSubmit}>
            <div style={{ backgroundColor: 'white' }} className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 w-full max-w-[958px] mx-auto gap-6 flex flex-col">
                <div className="inline-flex justify-start items-center gap-2">
                    <LucideLaptop className="w-6 h-6" color="#0053C6" />
                    <div className="justify-center text-gray-600 text-2xl font-bold font-['Inter']">Technology Check</div>
                </div>
                <div className="justify-center text-xs font-sora"><span className="text-red-500 font-bold leading-5">*</span><span className="leading-5"> </span><span className="text-gray-500 leading-5">Indicates required</span>
                </div>
                <div className="justify-center text-gray-600 text-sm font-normal font-sora leading-5">Students in online or remote courses may need to use a personal device that meets minimum technical requirements for academic success.</div>
                <div className="bg-gradient-to-b from-white to-sky-50 rounded-xl shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10)] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)] outline outline-1 outline-offset-[-1px] outline-gray-200 pt-6 gap-4 pb-10 max-sm:px-3">
                    {systemCheckStarted ? <SystemCheckPanel onComplete={handleSystemCheckComplete} onReset={handleSystemCheckReset} onBusyChange={setSystemCheckBusy} /> : (<>
                    <div className="w-10 h-10 mx-auto bg-sky-50 rounded-full pt-2">
                        <LucideRocket className="w-6 h-6 mx-auto" color="#0053C6" />
                    </div>
                    <div className="mx-auto text-center text-gray-800 text-xl font-semibold font-sora leading-6 mt-4">Open Speed Test</div>
                    <div className="mx-auto w-full max-w-[508px] text-center text-gray-600 text-sm font-normal font-sora leading-5">Please complete this device check on the computer you will use for coursework. Do not use a phone, tablet, or shared device, as the results may not reflect your primary system.</div>
                    {/* Required-task instructions (UAT): what is required, how long it takes, and when Submit becomes available. */}
                    <ul className="mx-auto w-full max-w-[508px] mt-3 list-disc pl-5 text-left text-gray-600 text-sm font-normal font-sora leading-5">
                        <li>This check is required to complete the Technology Check task.</li>
                        <li>It takes about 40 seconds. Keep this tab open until it finishes.</li>
                        <li>Please use Google Chrome for the most accurate results.</li>
                        <li>{MANUAL_UPLOAD_ENABLED
                            ? "Submit becomes available once the test has finished and you have answered whether your computer meets the requirements."
                            : "Submit becomes available once the test has finished and your results are ready."}</li>
                    </ul>
                    <div className="w-full flex justify-center">
                        <FormButton
                            intent="primary"
                            shape="cta"
                            className="mt-6 max-w-full font-semibold font-sora"
                            onClick={() => setSystemCheckStarted(true)}
                        >
                            Get Started
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6.6665 3.9974H3.99984C3.64622 3.9974 3.30708 4.13787 3.05703 4.38792C2.80698 4.63797 2.6665 4.97711 2.6665 5.33073V11.9974C2.6665 12.351 2.80698 12.6902 3.05703 12.9402C3.30708 13.1903 3.64622 13.3307 3.99984 13.3307H10.6665C11.0201 13.3307 11.3593 13.1903 11.6093 12.9402C11.8594 12.6902 11.9998 12.351 11.9998 11.9974V9.33073M9.33317 2.66406H13.3332M13.3332 2.66406V6.66406M13.3332 2.66406L6.6665 9.33073" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </FormButton>
                    </div>
                    </>)}
                </div>

                {MANUAL_UPLOAD_ENABLED && (<>
                <div className="justify-start text-gray-600 text-base font-bold font-['Inter'] leading-6">
                    Please take a screenshot and upload the results of the Speed Test
                </div>

                <DroppableFileUpload
                    uploadData={uploadData}
                    updateState={handleFileSelection}
                    accessibleFieldName="Speed Test Results"
                    showSuccessCard
                    uploadedAt={docStatus?.uploadedAt}
                    fileSizeBytes={docStatus?.sizeBytes}
                    previewUrl={applicantId ? enrollmentApi.getTechCheckPreviewUrl(applicantId) : undefined}
                />

                {/*TODO: CSS for disabled*/}
                <RadioSet
                    name="requirementsMet"
                    required={true}
                    onChange={handleRadioChange}
                    legend="Does your computer meet the requirements?"
                    legendClassAppend="color-neutral-600"
                    labelsClassAppend="text-gray-600 text-sm align-middle"
                    options={RadioSetOptions.YesNoTrueFalse}
                    disabled={!uploadData.filePicked}
                    initialValue={radioVal === null ? undefined : String(radioVal)}
                />
                </>)}

                {radioVal === true && (
                    <div className="flex items-start rounded-[10px] overflow-hidden">
                        <div className="w-2 self-stretch shrink-0 bg-green-600"></div>
                        <div
                            className="flex flex-1 gap-3 items-center px-3.5 py-3"
                            style={{ background: "#F0FDF4" }}
                        >
                            <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
                            <div className="flex flex-col text-[#3D444D]">
                                <p className="font-semibold text-[13px] font-sora">Your computer meets all of the minimum technical requirements.</p>
                                <p className="font-normal text-xs font-sora">Click the Submit button to save your test results.</p>
                            </div>
                        </div>
                    </div>
                )}

                {radioVal === false && (
                    <div className="flex items-start rounded-[10px] overflow-hidden">
                        <div className="w-2 self-stretch shrink-0" style={{ background: "#00AAFF" }}></div>
                        <div
                            className="flex flex-1 gap-3 items-center px-3.5 py-3"
                            style={{ background: "rgba(166,225,255,0.5)" }}
                        >
                            <Info className="h-5 w-5 shrink-0" color="#00AAFF" />
                            <div className="flex flex-col text-[#3D444D]">
                                <p className="font-semibold text-[13px] font-sora">Your computer does not meet all of the minimum technical requirements, please speak with your admissions representative.</p>
                                <p className="font-normal text-xs font-sora">We'll help you find the best path forward. Click the Submit button to save your test results.</p>
                            </div>
                        </div>
                    </div>
                )}

                {submitFailed && (
                    <div className="flex items-start rounded-[10px] overflow-hidden" role="alert">
                        <div className="w-2 self-stretch shrink-0" style={{ background: "#FF8409" }}></div>
                        <div
                            className="flex flex-1 gap-3 items-center px-3.5 py-3"
                            style={{ background: "#FFEBD5" }}
                        >
                            <AlertTriangle className="h-5 w-5 shrink-0" color="#FF8409" />
                            <div className="flex flex-col text-[#3D444D]">
                                <p className="font-semibold text-[13px] font-sora">We were unable to submit your test results at this time.</p>
                                <p className="font-normal text-xs font-sora">Please try again in a few minutes. If you are still having issues, please reach out to your advisor.</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="h-0 outline outline-1 w-full outline-neutral-200"></div>

                <div className="flex flex-col md:flex-row md:justify-between gap-4">
                    <FormButton
                        type="button"
                        intent="neutralBorder"
                        shape="nav"
                        onClick={() => navigate("/dashboard")}
                    >
                        Return to Dashboard
                    </FormButton>
                    <div className="form-nav flex flex-col md:flex-row gap-4">
                    <FormButton
                        type="button"
                        intent="neutral"
                        shape="nav"
                        disabled={submitting || systemCheckBusy}
                        onClick={handleSaveAndExit}
                    >
                        Save &amp; Exit
                    </FormButton>
                    {/*TODO: CSS for disabled*/}
                    <FormButton
                        type="submit"
                        intent="accent"
                        shape="nav"
                        disabled={disableSubmit || submitting}
                    >
                        Submit
                        <ChevronRight size={18} />
                    </FormButton>
                    </div>
                </div>
            </div>
            {showSubmittedModal && !submitFailed && <TechCheckSubmittedModal />}
        </form>
    );
};

export default TechnologyCheck;
